import {
  ApiError,
  type BulkActiveCustomersResult,
  CUSTOMER_ORIGINS,
  type CustomerOrigin,
  formatMoney,
  formatUsPhone,
  type MergeRecords,
  queryKeys,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Copy, Download, GitMerge, MessageSquare, Plus, Search, Upload, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { AddCustomerDialog } from '../components/add-customer-dialog.js'
import { Page, PageHeader } from '../components/app-shell.js'
import { CustomerImportDialog } from '../components/csv-import-dialog.js'
import { CustomerBulkMessageDialog } from '../components/customer-bulk-message-dialog.js'
import { CustomerMatchMergeDialog } from '../components/customer-match-merge-dialog.js'
import { BulkActionBar, DataTable } from '../components/data-table.js'
import { MergeCustomersDialog } from '../components/merge-customers-dialog.js'
import { api, apiBaseUrl } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface CustomerRow {
  id: string
  name: string
  businessName: string | null
  phone: string | null
  email: string | null
  isActive: boolean
  phoneSmsCapable: boolean
  orderCount: number
  balanceDueCents: number
  totalSpentCents: number
  lastActivityAt: string | null
  createdAt: string
  hasPotentialDuplicate: boolean
  hasSimilarName: boolean
  source: string | null
  origin: CustomerOrigin | null
  externalId: string | null
  smsReachable: boolean
}

const ORIGIN_LABEL: Record<CustomerOrigin, string> = {
  admin: 'Added in admin',
  web: 'Web signup',
  kiosk: 'Kiosk',
  import: 'Imported',
}

type PhoneFilter = 'all' | 'missing' | 'sms' | 'no-sms'
type EmailFilter = 'all' | 'missing'
type OriginFilter = 'all' | CustomerOrigin | 'unknown'
type StatusFilter = 'all' | 'active' | 'inactive'
type DuplicateFilter = 'all' | 'flagged' | 'similar'
type ExternalIdFilter = 'all' | 'has' | 'none'

export function CustomersPage() {
  const navigate = useNavigate()
  const { can } = useAuth()
  const [search, setSearch] = useState('')
  const [phoneFilter, setPhoneFilter] = useState<PhoneFilter>('all')
  const [emailFilter, setEmailFilter] = useState<EmailFilter>('all')
  const [originFilter, setOriginFilter] = useState<OriginFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [duplicateFilter, setDuplicateFilter] = useState<DuplicateFilter>('all')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [externalIdFilter, setExternalIdFilter] = useState<ExternalIdFilter>('all')
  const [sort, setSort] = useState<{ column: string; order: 'asc' | 'desc' }>({
    column: 'createdAt',
    order: 'desc',
  })
  const [adding, setAdding] = useState(false)
  const [importing, setImporting] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [messaging, setMessaging] = useState(false)
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false)
  const [mergingPair, setMergingPair] = useState<MergeRecords | null>(null)
  const [matchFor, setMatchFor] = useState<string | null>(null)

  // A selection is only meaningful against the result set it was made in —
  // changing a filter can drop rows out from under it silently.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberately resets only when a filter changes, not on every render
  useEffect(() => {
    setSelectedIds(new Set())
  }, [
    search,
    phoneFilter,
    emailFilter,
    originFilter,
    statusFilter,
    duplicateFilter,
    sourceFilter,
    externalIdFilter,
  ])

  const hasFilters =
    !!search ||
    phoneFilter !== 'all' ||
    emailFilter !== 'all' ||
    originFilter !== 'all' ||
    statusFilter !== 'all' ||
    duplicateFilter !== 'all' ||
    sourceFilter !== 'all' ||
    externalIdFilter !== 'all'

  const clearFilters = () => {
    setSearch('')
    setPhoneFilter('all')
    setEmailFilter('all')
    setOriginFilter('all')
    setStatusFilter('all')
    setDuplicateFilter('all')
    setSourceFilter('all')
    setExternalIdFilter('all')
  }

  // Add another filter here: a state above, a line below, and a Select in
  // the filter bar — nothing else needs to know about it.
  const filters = {
    q: search || undefined,
    missingPhone: phoneFilter === 'missing' ? true : phoneFilter === 'no-sms' ? false : undefined,
    smsCapable: phoneFilter === 'sms' ? true : phoneFilter === 'no-sms' ? false : undefined,
    missingEmail: emailFilter === 'missing' ? true : undefined,
    origin: originFilter === 'all' ? undefined : originFilter,
    isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
    flaggedDuplicate: duplicateFilter === 'flagged' ? true : undefined,
    duplicateTier: duplicateFilter === 'similar' ? 'name' : undefined,
    source: sourceFilter === 'all' ? undefined : sourceFilter,
    hasExternalId: externalIdFilter === 'all' ? undefined : externalIdFilter === 'has',
    sort: sort.column,
    order: sort.order,
    pageSize: 50,
  }

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.customers.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: CustomerRow[]; total: number; page: number; pageSize: number }>(
        '/customers',
        { query: { ...filters, page: pageParam } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const rows = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  // Distinct sources in use, for the Source filter (t1247).
  const { data: sourceData } = useQuery({
    queryKey: queryKeys.customers.sources(),
    queryFn: () => api.get<{ sources: string[] }>('/customers/sources'),
  })
  const sources = sourceData?.sources ?? []

  const toast = useToast()
  const queryClient = useQueryClient()
  const selectedRows = rows.filter((row) => selectedIds.has(row.id))

  const loadMergePair = useMutation({
    mutationFn: () => {
      const [a, b] = [...selectedIds]
      return api.get<MergeRecords>('/customers/merge-records', { query: { a, b } })
    },
    onSuccess: setMergingPair,
    onError: (error: Error) =>
      toast.error(
        'Could not load these customers',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const bulkActive = useMutation({
    mutationFn: (isActive: boolean) =>
      api.post<BulkActiveCustomersResult>('/customers/bulk-active', {
        customerIds: [...selectedIds],
        isActive,
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all() })
      setSelectedIds(new Set())
      setConfirmingDeactivate(false)
      if (result.failed.length === 0) {
        toast.success(
          `${result.updated.length} customer${result.updated.length === 1 ? '' : 's'} updated`,
        )
      } else {
        toast.toast({
          tone: 'warning',
          title: `${result.updated.length} updated, ${result.failed.length} could not be`,
          description: result.failed[0]?.reason,
        })
      }
    },
    onError: (error: Error) =>
      toast.error(
        'Could not update those customers',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const canWrite = can('customers:write')
  const columns = useMemo<ColumnDef<CustomerRow, unknown>[]>(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        meta: { sortKey: 'name' },
        cell: ({ row }) => (
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium">{row.original.name}</span>
              {!row.original.isActive ? <Badge tone="neutral">Inactive</Badge> : null}
              {row.original.hasPotentialDuplicate || row.original.hasSimilarName ? (
                canWrite ? (
                  <button
                    type="button"
                    title="Review matches and merge"
                    className="inline-flex items-center gap-1 rounded-full hover:opacity-80 focus-visible:outline focus-visible:outline-2"
                    onClick={(e) => {
                      e.stopPropagation()
                      setMatchFor(row.original.id)
                    }}
                  >
                    <Badge tone={row.original.hasPotentialDuplicate ? 'warning' : 'neutral'}>
                      {row.original.hasPotentialDuplicate ? 'Possible duplicate' : 'Similar name'}
                      <GitMerge className="ms-1 size-3" aria-hidden="true" />
                    </Badge>
                    <span className="sr-only">Review matches and merge</span>
                  </button>
                ) : (
                  <Badge tone={row.original.hasPotentialDuplicate ? 'warning' : 'neutral'}>
                    {row.original.hasPotentialDuplicate ? 'Possible duplicate' : 'Similar name'}
                  </Badge>
                )
              ) : null}
            </div>
            {row.original.businessName ? (
              <span className="block text-xs text-foreground-muted">
                {row.original.businessName}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        header: 'Phone',
        accessorKey: 'phone',
        meta: { sortKey: 'phone' },
        cell: ({ row }) =>
          row.original.phone ? (
            <span className="flex items-center gap-1.5">
              <span className="font-identifier">{formatUsPhone(row.original.phone)}</span>
              {row.original.phoneSmsCapable ? <Badge tone="neutral">Texts</Badge> : null}
            </span>
          ) : (
            <span className="text-foreground-subtle">—</span>
          ),
      },
      {
        header: 'Email',
        accessorKey: 'email',
        meta: { sortKey: 'email' },
        cell: ({ row }) => (
          <span className="text-foreground-muted">{row.original.email ?? '—'}</span>
        ),
      },
      {
        header: 'Source',
        accessorKey: 'source',
        cell: ({ row }) =>
          row.original.source || row.original.externalId ? (
            <div className="text-xs">
              <span className="block text-foreground-muted">{row.original.source ?? '—'}</span>
              {row.original.externalId ? (
                <span className="block font-identifier text-foreground-subtle">
                  {row.original.externalId}
                </span>
              ) : null}
            </div>
          ) : (
            <span className="text-foreground-subtle">—</span>
          ),
      },
      {
        header: 'Origin',
        accessorKey: 'origin',
        cell: ({ row }) =>
          row.original.origin ? (
            <span className="text-xs text-foreground-muted">
              {ORIGIN_LABEL[row.original.origin]}
            </span>
          ) : (
            <span className="text-xs text-foreground-subtle">Unknown</span>
          ),
      },
      {
        header: 'Orders',
        accessorKey: 'orderCount',
        meta: { sortKey: 'orderCount' },
        cell: ({ row }) => <span className="tabular">{row.original.orderCount}</span>,
      },
      {
        header: 'Total spent',
        accessorKey: 'totalSpentCents',
        meta: { sortKey: 'totalSpentCents' },
        cell: ({ row }) =>
          row.original.totalSpentCents > 0 ? (
            <span className="tabular">{formatMoney(row.original.totalSpentCents)}</span>
          ) : (
            <span className="text-foreground-subtle">—</span>
          ),
      },
      {
        header: 'Balance due',
        accessorKey: 'balanceDueCents',
        meta: { sortKey: 'balanceDueCents' },
        cell: ({ row }) =>
          row.original.balanceDueCents > 0 ? (
            <span className="tabular font-medium text-warning">
              {formatMoney(row.original.balanceDueCents)}
            </span>
          ) : (
            <span className="text-foreground-subtle">—</span>
          ),
      },
      {
        header: 'Last activity',
        accessorKey: 'lastActivityAt',
        meta: { sortKey: 'lastActivityAt' },
        cell: ({ row }) => (
          <span className="text-xs text-foreground-muted">
            {row.original.lastActivityAt
              ? new Date(row.original.lastActivityAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : 'Never'}
          </span>
        ),
      },
    ],
    [canWrite],
  )

  return (
    <Page>
      <PageHeader
        title="Customers"
        description="Search by name, business or other identifier, phone number, or email."
        actions={
          can('customers:write') ? (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  const params = new URLSearchParams()
                  if (filters.q) params.set('q', filters.q)
                  if (filters.missingPhone !== undefined) {
                    params.set('missingPhone', String(filters.missingPhone))
                  }
                  if (filters.smsCapable !== undefined) {
                    params.set('smsCapable', String(filters.smsCapable))
                  }
                  if (filters.missingEmail !== undefined) {
                    params.set('missingEmail', String(filters.missingEmail))
                  }
                  if (filters.origin) params.set('origin', filters.origin)
                  if (filters.source) params.set('source', filters.source)
                  if (filters.hasExternalId !== undefined) {
                    params.set('hasExternalId', String(filters.hasExternalId))
                  }
                  if (filters.isActive !== undefined) {
                    params.set('isActive', String(filters.isActive))
                  }
                  params.set('sort', filters.sort)
                  params.set('order', filters.order)
                  const qs = params.toString()
                  window.location.assign(`${apiBaseUrl}/customers/export.csv${qs ? `?${qs}` : ''}`)
                }}
              >
                <Download /> Export
              </Button>
              <Button variant="secondary" onClick={() => setImporting(true)}>
                <Upload /> Import
              </Button>
              <Button
                variant="secondary"
                onClick={() => void navigate({ to: '/customers/cleanup' })}
              >
                <Copy /> Duplicates & cleanup
              </Button>
              <Button variant="primary" onClick={() => setAdding(true)}>
                <Plus /> Add customer
              </Button>
            </div>
          ) : null
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative max-w-xs flex-1">
          <Search
            className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground-subtle"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, identifier, or phone…"
            className="ps-8"
          />
        </div>
        <Select value={phoneFilter} onValueChange={(value) => setPhoneFilter(value as PhoneFilter)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All phones</SelectItem>
            <SelectItem value="missing">Missing phone</SelectItem>
            <SelectItem value="sms">Can receive texts</SelectItem>
            <SelectItem value="no-sms">Can't receive texts</SelectItem>
          </SelectContent>
        </Select>
        <Select value={emailFilter} onValueChange={(value) => setEmailFilter(value as EmailFilter)}>
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All emails</SelectItem>
            <SelectItem value="missing">Missing email</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={originFilter}
          onValueChange={(value) => setOriginFilter(value as OriginFilter)}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All origins</SelectItem>
            {CUSTOMER_ORIGINS.map((origin) => (
              <SelectItem key={origin} value={origin}>
                {ORIGIN_LABEL[origin]}
              </SelectItem>
            ))}
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as StatusFilter)}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={duplicateFilter}
          onValueChange={(value) => setDuplicateFilter(value as DuplicateFilter)}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All records</SelectItem>
            <SelectItem value="flagged">Possible duplicates</SelectItem>
            <SelectItem value="similar">Same or similar name</SelectItem>
          </SelectContent>
        </Select>
        {sources.length > 0 ? (
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {sources.map((source) => (
                <SelectItem key={source} value={source}>
                  {source}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <Select
          value={externalIdFilter}
          onValueChange={(value) => setExternalIdFilter(value as ExternalIdFilter)}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any external ID</SelectItem>
            <SelectItem value="has">Has external ID</SelectItem>
            <SelectItem value="none">No external ID</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters ? (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : null}
      </div>

      {(phoneFilter === 'missing' || emailFilter === 'missing') && total > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface-sunken px-3 py-2 text-sm">
          <span>
            <strong>{total.toLocaleString()}</strong> customer{total === 1 ? '' : 's'} with{' '}
            {phoneFilter === 'missing' && emailFilter === 'missing'
              ? 'no phone and no email'
              : phoneFilter === 'missing'
                ? 'no phone number'
                : 'no email'}
            .
          </span>
          {rows[0] ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                void navigate({
                  to: '/customers/$customerId',
                  params: { customerId: rows[0]?.id ?? '' },
                })
              }
            >
              Open the first one
            </Button>
          ) : null}
          <span className="text-xs text-foreground-muted">
            Fill in the missing details; the customer drops off this list once they are added.
          </span>
        </div>
      ) : null}

      <DataTable
        data={rows}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(row) =>
          void navigate({ to: '/customers/$customerId', params: { customerId: row.id } })
        }
        {...(can('customers:write')
          ? {
              getRowId: (row: CustomerRow) => row.id,
              selectedIds,
              onToggleRow: (id: string) =>
                setSelectedIds((prev) => {
                  const next = new Set(prev)
                  if (next.has(id)) next.delete(id)
                  else next.add(id)
                  return next
                }),
              onToggleAll: (ids: string[]) =>
                setSelectedIds((prev) => (prev.size === ids.length ? new Set() : new Set(ids))),
            }
          : {})}
        sort={{
          column: sort.column,
          order: sort.order,
          onChange: (column, order) => setSort({ column, order }),
        }}
        infinite={{
          total,
          hasMore: Boolean(hasNextPage),
          isFetchingMore: isFetchingNextPage,
          onLoadMore: () => void fetchNextPage(),
        }}
        empty={
          hasFilters ? (
            <EmptyState
              icon={Search}
              variant="no-results"
              title="No customers match"
              description="Try different search or filters."
              onClearFilters={clearFilters}
            />
          ) : (
            <EmptyState
              icon={Users}
              title="No customers yet"
              description="Customers appear here once they buy something or sign in."
            />
          )
        }
      />

      {can('customers:write') ? (
        <BulkActionBar count={selectedIds.size} onClear={() => setSelectedIds(new Set())}>
          {selectedIds.size > 200 ? (
            <span className="text-xs text-foreground-muted">
              Bulk actions work on up to 200 customers at a time
            </span>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => setMessaging(true)}>
                <MessageSquare /> Send text…
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={bulkActive.isPending}
                onClick={() => bulkActive.mutate(true)}
              >
                Activate
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={bulkActive.isPending}
                onClick={() => setConfirmingDeactivate(true)}
              >
                Deactivate
              </Button>
              {selectedIds.size === 2 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  loading={loadMergePair.isPending}
                  onClick={() => loadMergePair.mutate()}
                >
                  Merge…
                </Button>
              ) : null}
            </>
          )}
        </BulkActionBar>
      ) : null}

      {mergingPair ? (
        <MergeCustomersDialog
          a={mergingPair.a}
          b={mergingPair.b}
          onClose={() => {
            setMergingPair(null)
            setSelectedIds(new Set())
          }}
        />
      ) : null}

      {matchFor ? (
        <CustomerMatchMergeDialog customerId={matchFor} onClose={() => setMatchFor(null)} />
      ) : null}

      <Dialog
        open={confirmingDeactivate}
        onOpenChange={(open) => !open && setConfirmingDeactivate(false)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Deactivate {selectedIds.size} customer{selectedIds.size === 1 ? '' : 's'}?
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              They can no longer sign in or check out online. Order history is unaffected, and they
              can be reactivated later.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmingDeactivate(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={bulkActive.isPending}
              onClick={() => bulkActive.mutate(false)}
            >
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomerBulkMessageDialog
        open={messaging}
        onClose={() => setMessaging(false)}
        customers={selectedRows.map((row) => ({
          id: row.id,
          name: row.name,
          smsReachable: row.smsReachable,
        }))}
        onSent={(sentIds) =>
          setSelectedIds((prev) => {
            const next = new Set(prev)
            for (const id of sentIds) next.delete(id)
            return next
          })
        }
      />

      <AddCustomerDialog
        open={adding}
        onOpenChange={setAdding}
        onCreated={(customer) =>
          void navigate({ to: '/customers/$customerId', params: { customerId: customer.id } })
        }
        existingAction={{
          label: 'Open their page',
          onSelect: (customer) =>
            void navigate({ to: '/customers/$customerId', params: { customerId: customer.id } }),
        }}
      />
      <CustomerImportDialog open={importing} onOpenChange={setImporting} />
    </Page>
  )
}
